import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { apiFetch } from '../../../utils/api';
import { useToast } from '../../../components/ui/Toast';
import './inventory-tracker.css';

const TYPE_OPTIONS = [
  { value: '', label: 'All events' },
  { value: 'RECEIVED', label: 'Received' },
  { value: 'SOLD', label: 'Sold' },
  { value: 'TRANSFER_OUT', label: 'Transferred out' },
  { value: 'TRANSFER_IN', label: 'Transferred in' },
  { value: 'WASTAGE', label: 'Waste / loss' },
  { value: 'RESERVED', label: 'Reserved (active)' },
  { value: 'RESERVED_CONSUMED', label: 'Reservation consumed' },
  { value: 'RESERVED_RELEASED', label: 'Reservation released' },
  { value: 'RESERVED_EXPIRED', label: 'Reservation expired' },
];

const CHANNEL_OPTIONS = [
  { value: '', label: 'All channels' },
  { value: 'STOREFRONT', label: 'Storefront' },
  { value: 'POS', label: 'POS' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'MANUAL', label: 'Manual' },
];

const badgeClassFor = (type) => {
  if (type === 'RECEIVED') return 'received';
  if (type === 'SOLD') return 'sold';
  if (type === 'TRANSFER_OUT' || type === 'TRANSFER_IN') return 'transfer';
  if (type === 'WASTAGE') return 'wastage';
  return 'reservation';
};

const humanType = (type) =>
  type
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const formatDelta = (n) => (n > 0 ? `+${n}` : `${n}`);

const initials = (name) => {
  if (!name) return '—';
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
};

const formatDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return d.toLocaleString('en-NG', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

/**
 * Reverse-compute "balance after event" per product by walking the
 * newest-first event list and subtracting each event's delta from the
 * running balance. Requires productQuantities (current qty per product
 * on the page) from the backend.
 *
 * If a product isn't in productQuantities (e.g., soft-deleted), we
 * simply annotate `null` — the row still renders but shows "—".
 *
 * Algorithm (per product):
 *   balance_after(newest event) = current_qty
 *   balance_after(event i) = balance_after(event i+1 newer) - event_(i+1).delta
 * Or equivalently walking top-down:
 *   after we render row 0, subtract its delta before rendering row 1.
 */
function annotateBalances(events, productQuantities = {}) {
  const runningPerProduct = { ...productQuantities };
  return events.map((e) => {
    const pid = e.productId;
    if (!pid || runningPerProduct[pid] == null) {
      return { ...e, balanceAfter: null };
    }
    const balanceAfter = runningPerProduct[pid];
    runningPerProduct[pid] = balanceAfter - Number(e.delta || 0);
    return { ...e, balanceAfter };
  });
}

const EventDetails = ({ event }) => (
  <div style={{ background: '#fafbfc', padding: '12px 18px', borderTop: '1px solid #e5e7eb', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
    <div>
      <p style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', margin: '0 0 4px' }}>Event</p>
      <div style={{ fontSize: '0.85rem' }}>{event.eventId}</div>
      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>{formatDate(event.ts)}</div>
    </div>
    <div>
      <p style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', margin: '0 0 4px' }}>Product</p>
      <div style={{ fontSize: '0.85rem' }}>{event.productName || event.productId?.slice(0, 12) || '—'}</div>
      {event.variantId && (
        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>
          Variant: {event.metadata?.variantName || event.variantId.slice(0, 8)}
        </div>
      )}
    </div>
    <div>
      <p style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', margin: '0 0 4px' }}>Stock effect</p>
      <div style={{ fontSize: '0.85rem' }}>
        Delta: <span className={`itk-delta ${event.delta > 0 ? 'positive' : event.delta < 0 ? 'negative' : 'zero'}`}>{formatDelta(event.delta)}</span>
      </div>
      {event.balanceAfter != null && (
        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>
          Balance after: {event.balanceAfter.toLocaleString()}
        </div>
      )}
    </div>
    {event.batchId && (
      <div>
        <p style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', margin: '0 0 4px' }}>Batch</p>
        <div style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>{event.batchId.slice(0, 12)}</div>
        {event.costPerUnit != null && (
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>
            Cost/unit: ₦{Number(event.costPerUnit).toLocaleString()}
          </div>
        )}
      </div>
    )}
    {event.metadata?.orderNumber && (
      <div>
        <p style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', margin: '0 0 4px' }}>Order</p>
        <div style={{ fontSize: '0.85rem' }}>
          <a
            href={`/orders/${event.source.id}`}
            style={{ color: '#0d7463', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            {event.metadata.orderNumber} <ExternalLink size={11} />
          </a>
        </div>
        {event.metadata.channel && (
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>Channel: {event.metadata.channel}</div>
        )}
      </div>
    )}
    {event.actor && (
      <div>
        <p style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', margin: '0 0 4px' }}>Actor</p>
        <div style={{ fontSize: '0.85rem' }}>{event.actor.name}</div>
        {event.actor.role && (
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>{event.actor.role}</div>
        )}
      </div>
    )}
    {event.locationId && (
      <div>
        <p style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', margin: '0 0 4px' }}>Location</p>
        <div style={{ fontSize: '0.85rem' }}>{event.locationName || event.locationId.slice(0, 12)}</div>
        {event.locationCode && (
          <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 2 }}>Code: {event.locationCode}</div>
        )}
      </div>
    )}
    {(event.metadata?.toLocationName || event.metadata?.fromLocationName) && (
      <div>
        <p style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', margin: '0 0 4px' }}>Transfer route</p>
        <div style={{ fontSize: '0.85rem' }}>
          {event.metadata.fromLocationName || '—'} → {event.metadata.toLocationName || '—'}
        </div>
      </div>
    )}
    {event.metadata && Object.keys(event.metadata).filter((k) => !['productName', 'orderNumber', 'channel', 'variantName'].includes(k)).length > 0 && (
      <div style={{ gridColumn: '1 / -1' }}>
        <p style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', margin: '0 0 4px' }}>Additional metadata</p>
        <pre style={{ margin: 0, fontSize: '0.72rem', background: '#ffffff', border: '1px solid #e5e7eb', padding: '8px 10px', borderRadius: 6, overflow: 'auto' }}>
          {JSON.stringify(
            Object.fromEntries(
              Object.entries(event.metadata).filter(([k]) => !['productName', 'orderNumber', 'channel', 'variantName'].includes(k))
            ),
            null, 2
          )}
        </pre>
      </div>
    )}
  </div>
);

/**
 * Movement Log tab — merchant-facing unified feed over the existing
 * inventory tables. Reads `GET /merchants/inventory/movements` (backed
 * by stockMovementReader). Cursor pagination via loadMore.
 *
 * Rows are click-to-expand — each expanded row shows full event
 * context (batch, order, actor, metadata) and the running balance
 * after the event for that product.
 *
 * Accepts an optional `initialProductId` so links from the product
 * detail page can pre-filter to a single SKU.
 */
const MovementLogTab = ({ initialProductId = '' } = {}) => {
  const toast = useToast();
  const [events, setEvents] = useState([]);
  const [productQuantities, setProductQuantities] = useState({});
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [filters, setFilters] = useState({
    type: '',
    channel: '',
    productId: initialProductId || '',
  });

  const loadPage = useCallback(async (opts = {}) => {
    const isMore = !!opts.cursor;
    if (isMore) setLoadingMore(true); else setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.type) params.set('type', filters.type);
      if (filters.channel) params.set('channel', filters.channel);
      if (filters.productId) params.set('productId', filters.productId);
      params.set('limit', '25');
      if (opts.cursor) params.set('cursor', opts.cursor);
      const res = await apiFetch(`/merchants/inventory/movements?${params.toString()}`);
      const payload = await res.json();
      if (!res.ok || !payload.success) {
        throw new Error(payload.error || 'Unable to load movements');
      }
      setEvents((prev) => (isMore ? [...prev, ...payload.events] : payload.events));
      // Merge product quantities across paginated pages; newer page
      // wins for any product ID that appears in both (its "current"
      // will be fresher).
      setProductQuantities((prev) => (isMore ? { ...prev, ...payload.productQuantities } : payload.productQuantities || {}));
      setCursor(payload.nextCursor);
      if (!isMore) setExpanded(new Set());
    } catch (e) {
      toast.error(e.message || 'Unable to load movements');
    } finally {
      if (isMore) setLoadingMore(false); else setLoading(false);
    }
  }, [filters, toast]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const handleFilter = (key) => (e) => setFilters((prev) => ({ ...prev, [key]: e.target.value }));

  const toggleRow = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Compute balance-after per event before rendering. Recomputed
  // whenever events or productQuantities change; O(n).
  const eventsWithBalance = useMemo(
    () => annotateBalances(events, productQuantities),
    [events, productQuantities]
  );

  return (
    <div className="itk-panel">
      <div className="itk-panel-header">
        <div>
          <h2>Movement log</h2>
          <p>Every stock event — received, sold, transferred, waste, reservations — across all channels and locations. Click any row for full detail.</p>
        </div>
        <button
          type="button"
          className="itk-btn"
          onClick={() => loadPage()}
          disabled={loading}
        >
          {loading ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </div>

      <div className="itk-toolbar">
        <select value={filters.type} onChange={handleFilter('type')} aria-label="Filter by event type">
          {TYPE_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
        </select>
        <select value={filters.channel} onChange={handleFilter('channel')} aria-label="Filter by channel (SOLD only)">
          {CHANNEL_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
        </select>
        <input
          type="text"
          placeholder="Product ID (optional)"
          value={filters.productId}
          onChange={handleFilter('productId')}
          aria-label="Filter by product ID"
        />
      </div>

      {loading ? (
        <div className="itk-loading"><Loader2 size={18} className="spin" /> Loading movements…</div>
      ) : eventsWithBalance.length === 0 ? (
        <div className="itk-empty">
          <strong>No inventory movements in this range</strong>
          Try changing filters or expanding the date window.
        </div>
      ) : (
        <>
          <div className="itk-scroll">
            <table className="itk-table">
              <thead>
                <tr>
                  <th style={{ width: 28 }}></th>
                  <th>Type</th>
                  <th>When</th>
                  <th>Product</th>
                  <th>Delta</th>
                  <th>Balance after</th>
                  <th>Cost / unit</th>
                  <th>Location</th>
                  <th>Actor</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {eventsWithBalance.map((e) => (
                  <React.Fragment key={e.eventId}>
                    <tr
                      onClick={() => toggleRow(e.eventId)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        {expanded.has(e.eventId)
                          ? <ChevronDown size={14} color="#6b7280" />
                          : <ChevronRight size={14} color="#6b7280" />}
                      </td>
                      <td>
                        <span className={`itk-badge ${badgeClassFor(e.type)}`}>{humanType(e.type)}</span>
                      </td>
                      <td className="muted">{formatDate(e.ts)}</td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span>{e.productName || e.productId?.slice(0, 8) || '—'}</span>
                          {e.metadata?.variantName && (
                            <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>{e.metadata.variantName}</span>
                          )}
                          {e.metadata?.unbatched && (
                            <span style={{ color: '#d97706', fontSize: '0.7rem', fontWeight: 500 }}>Unbatched (legacy)</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`itk-delta ${e.delta > 0 ? 'positive' : e.delta < 0 ? 'negative' : 'zero'}`}>
                          {formatDelta(e.delta)}
                        </span>
                      </td>
                      <td>{e.balanceAfter != null ? e.balanceAfter.toLocaleString() : <span className="muted">—</span>}</td>
                      <td className="muted">{e.costPerUnit != null ? `₦${Number(e.costPerUnit).toLocaleString()}` : '—'}</td>
                      <td className="muted">{e.locationName || (e.locationId ? e.locationId.slice(0, 8) : '—')}</td>
                      <td>
                        {e.actor ? (
                          <span className="itk-actor">
                            <span className="itk-actor-avatar">{initials(e.actor.name)}</span>
                            {e.actor.name}
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="muted">
                        {e.metadata?.orderNumber ? (
                          <span>{e.metadata.orderNumber}{e.metadata.channel ? ` · ${e.metadata.channel}` : ''}</span>
                        ) : (
                          <span>{e.source?.type?.toLowerCase()}</span>
                        )}
                      </td>
                    </tr>
                    {expanded.has(e.eventId) && (
                      <tr>
                        <td colSpan={10} style={{ padding: 0, borderBottom: '1px solid #f1f5f9' }}>
                          <EventDetails event={e} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {cursor && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                type="button"
                className="itk-btn"
                onClick={() => loadPage({ cursor })}
                disabled={loadingMore}
              >
                {loadingMore ? <Loader2 size={14} className="spin" /> : <ChevronDown size={14} />}
                Load more
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MovementLogTab;
